import {
  Component,
  OnInit,
  Input,
  HostBinding,
  Output,
  EventEmitter,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  Renderer2,
  ViewChild,
  HostListener,
  ViewEncapsulation,
  ContentChild,
  QueryList,
  AfterContentInit,
  ContentChildren,
} from '@angular/core';

import { trigger, transition } from '@angular/animations';
import {
  transitionDisplayMetadata,
  transitionDismissableContentMetadata,
} from './pg-notification-animations';

import {
  PgNotificationChild,
  PgNotificationTitleDirective,
} from './pg-notification-projection-elements';

export type NotificationType = 'proximity' | 'page' | 'flag';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PgPalette } from '../core/ui/palette';
import { Platform } from '@angular/cdk/platform';
@Component({
  selector: 'pg-notification, grit-notification',
  templateUrl: './pg-notification.component.html',
  styleUrls: ['./pg-notification.component.scss'],
  animations: [
    trigger('transitionDisplay', transitionDisplayMetadata),
    trigger(
      'transitionDismissableContent',
      transitionDismissableContentMetadata
    ),
  ],
  encapsulation: ViewEncapsulation.None,
})
export class PgNotificationComponent
  implements OnInit, AfterViewInit, OnChanges, AfterContentInit {
  @Input() color: PgPalette;

  @Input() action: 'collapse' | 'dismiss' = 'dismiss';

  @Input() modifier: 'bold' | null;

  @Input() type: NotificationType;

  @Input()
  elevation = 3;

  @Input() display: boolean;
  @Output() displayChange = new EventEmitter<boolean>();

  @Input() expand = true;
  @Output() expandChange = new EventEmitter<boolean>();

  @Output()
  dismissClick = new EventEmitter<void>();

  @Input() preventDefaultDismissBehavior = false;

  @ViewChild('notificationWrapper', { static: true })
  _notificationWrapperChild: ElementRef;
  get notificationWrapperEl(): HTMLElement {
    return this._notificationWrapperChild.nativeElement;
  }

  @ViewChild('dismissableContent', { static: true })
  _dismissableContentChild: ElementRef;
  get dismissableContentEl(): HTMLElement {
    return this._dismissableContentChild.nativeElement;
  }

  @ContentChildren(PgNotificationChild)
  _notificationChildren: QueryList<PgNotificationChild>;

  @ContentChild(PgNotificationTitleDirective, { static: false })
  notificationTitle: PgNotificationTitleDirective;

  cachedNotificationWrapperHeight = 0;
  cachedDismissableContentHeight = 0;
  cachedNotificationHeaderHeight = 0;

  @HostBinding('@transitionDisplay')
  get displayState() {
    let stateValue = 'hidden';
    if (this.display && this.expand) {
      stateValue = 'expanded';
    } else if (this.display) {
      stateValue = 'collapsed';
    }
    return {
      value: stateValue,
      params: {
        notificationWrapperHeight: this.cachedNotificationWrapperHeight,
        notificationHeaderHeight: this.cachedNotificationHeaderHeight,
        dismissableContentHeight: this.cachedDismissableContentHeight,
      },
    };
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resize$.next();
  }

  resize$ = new Subject<void>();

  get hostEl(): HTMLElement {
    return this.elementRef.nativeElement;
  }
  constructor(
    public elementRef: ElementRef,
    private _renderer: Renderer2,
    private _platform: Platform,
    private _cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {}

  async ngAfterViewInit() {
    this._cacheElementHeightsForAnimations(0);
    this.resize$.pipe(debounceTime(150)).subscribe(() => {
      this._cacheElementHeightsForAnimations(0);
    });
  }

  ngAfterContentInit() {
    this._notificationChildren.changes.subscribe(() => {
      this._cacheElementHeightsForAnimations(0);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.elevation) {
      this._renderer.removeClass(
        this.hostEl,
        `pg-elevation-${changes.elevation.previousValue}`
      );
      this._renderer.addClass(
        this.hostEl,
        `pg-elevation-${changes.elevation.currentValue}`
      );
    }
    if (changes.type) {
      this._cacheElementHeightsForAnimations();
    }
  }

  handleDismissIconClick() {
    if (this.preventDefaultDismissBehavior) {
      this.dismissClick.emit();
    } else {
      this.display = false;
    }
  }

  public async _cacheElementHeightsForAnimations(timeout = 250) {
    return new Promise((res, rej) => {
      setTimeout(() => {
        this.cachedNotificationWrapperHeight = this.notificationWrapperEl.offsetHeight;
        this.cachedDismissableContentHeight = this.dismissableContentEl.offsetHeight;
        this.cachedNotificationHeaderHeight = Math.max(
          64,
          this.cachedNotificationWrapperHeight -
            this.cachedDismissableContentHeight
        );
        res();
      }, timeout);
    });
  }

  async show() {
    await this._cacheElementHeightsForAnimations(0);
    this.display = true;
    this.displayChange.emit(this.display);
  }

  async hide() {
    this.display = false;
    this.displayChange.emit(this.display);
  }

  /*
    Safari is exhibiting weird behaviors with the standard declarative
    animations, so at the end of the transitionDisplay animations
    we will set the element animation style to null and this seems to fix the problem
  */
  @HostListener('@transitionDisplay.done', ['$event'])
  handleAnimationDone(event) {
    if (this._platform.SAFARI || this._platform.IOS) {
      this.hostEl.style.animation = null;
    }
  }
}
